import { useState, useCallback } from 'react';
import { Scenario, Scene, validateScenarioStructure, type InventoryEntry } from '../types';
import { ensureV2Scenario } from './migrate-v1-to-v2';
import { apiFetch } from './api-client';
import { getScenarioAssetCacheKey, resolveScenarioAssetUrl, type ScenarioAssetLayer } from './scenario-asset-url';
import { getActiveWorkspaceHeaders } from './workspace-context';
import {
  collectEntrySceneAssetUrls,
  collectRestOfChapterAssetUrls,
  prefetchAssetUrls,
  preloadAssetUrls,
  resolveEntrySceneId,
} from './scenario-assets';

export interface UseScenarioLoaderOptions {
  apiBaseUrl?: string;
  onLoadStart?: () => void;
  onLoadComplete?: (scenario: Scenario, scenarioId: string, missionId?: string, layer?: ScenarioAssetLayer) => void;
  onLoadError?: (error: Error, scenarioId: string) => void;
}

export interface LoadingState {
  isLoading: boolean;
  progress: number;
  error: Error | null;
  errorDetails?: string;
}

export function useScenarioLoader(options: UseScenarioLoaderOptions = {}) {
  const { 
    apiBaseUrl = '/api',
    onLoadStart,
    onLoadComplete,
    onLoadError
  } = options;

  const [loadingState, setLoadingState] = useState<LoadingState>({
    isLoading: false,
    progress: 0,
    error: null,
  });

  /**
   * Resolves all relative asset paths in a scenario to absolute URLs
   */
  const resolveAssetPaths = (scenario: Scenario, scenarioId: string, layer: ScenarioAssetLayer = 'master'): Scenario => {
    const origin = apiBaseUrl.replace('/api', '');
    const cacheKey = getScenarioAssetCacheKey(scenario);

    const resolve = (url: string | undefined): string | undefined =>
      resolveScenarioAssetUrl(url, scenarioId, cacheKey, { origin, layer });

    const resolveInventoryEntry = (entry: InventoryEntry): InventoryEntry => ({
      ...entry,
      image: resolve(entry.image),
    });

    const resolveScene = (scene: Scene): Scene => {
      const resolved: Scene = {
        ...scene,
        background: resolve(scene.background),
        elements: scene.elements?.map((element) => {
          const resolvedMoods = element.moods ? { ...element.moods } : undefined;
          if (resolvedMoods) {
            for (const mood in resolvedMoods) {
              resolvedMoods[mood] = resolve(resolvedMoods[mood])!;
            }
          }
          return {
            ...element,
            image: resolve(element.image),
            moods: resolvedMoods,
          };
        }),
      };
      if (resolved.type === 'choice') {
        resolved.options = resolved.options?.map((option) => ({
          ...option,
          scenes: option.scenes?.map(resolveScene),
        }));
      }
      if (resolved.type === 'textOverlay') {
        resolved.characterIcon = resolve(resolved.characterIcon);
      }
      if (resolved.type === 'phoneCall' || resolved.type === 'phoneChat') {
        resolved.contactAvatar = resolve(resolved.contactAvatar);
      }
      return resolved;
    };

    return {
      ...scenario,
      metadata: {
        ...scenario.metadata,
        thumbnail: resolve(scenario.metadata.thumbnail) || '',
        skills: scenario.metadata.skills?.map(skill => ({
          ...skill,
          icon: resolve(skill.icon),
        })),
        characters: scenario.metadata.characters?.map((char) => ({
          ...char,
          defaultImage: resolve(char.defaultImage) ?? char.defaultImage,
          icon: resolve(char.icon),
          emotions: char.emotions
            ? Object.fromEntries(
                Object.entries(char.emotions).map(([key, path]) => [key, resolve(path)!])
              )
            : undefined,
        })),
        inventory: scenario.metadata.inventory
          ? {
              ...scenario.metadata.inventory,
              entries: scenario.metadata.inventory.entries.map(resolveInventoryEntry),
            }
          : undefined,
      },
      items: scenario.items.map(item => ({
        ...item,
        icon: resolve(item.icon) || '',
      })),
      scenes: scenario.scenes.map(resolveScene),
    };
  };

  const loadScenario = useCallback(async (
    scenarioId: string,
    missionId?: string,
    layer: ScenarioAssetLayer = 'master',
    editorCatalogMode = false
  ) => {
    setLoadingState({
      isLoading: true,
      progress: 0,
      error: null,
    });
    
    onLoadStart?.();
    
    try {
      const query = new URLSearchParams();
      if (layer === 'draft') query.set('layer', 'draft');
      if (editorCatalogMode) query.set('all', 'true');
      const queryString = query.toString();
      const response = await apiFetch(
        `${apiBaseUrl}/scenarios/${scenarioId}${queryString ? `?${queryString}` : ''}`,
        {
          headers: {
            ...getActiveWorkspaceHeaders(),
          },
        }
      );

      if (response.status === 401) return;

      if (response.status === 403) {
        const data = await response.clone().json().catch(() => ({})) as { error?: string };
        if (data.error === 'Invalid or expired token') return;
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to load scenario: ${response.statusText}`
        );
      }
      
      let scenarioData = ensureV2Scenario(await response.json());
      
      const validation = validateScenarioStructure(scenarioData);
      if (!validation.isValid) {
        const errorDetails = validation.errors
          .map(err => `- ${err.path || err.field}: ${err.message}`)
          .join('\n');
        
        setLoadingState({
          isLoading: false,
          progress: 0,
          error: new Error('Invalid scenario format'),
          errorDetails,
        });
        
        const error = new Error('Invalid scenario format');
        onLoadError?.(error, scenarioId);
        return;
      }
      
      scenarioData = resolveAssetPaths(scenarioData, scenarioId, layer);

      const entrySceneId = resolveEntrySceneId(scenarioData, scenarioId, missionId);
      await preloadAssetUrls(collectEntrySceneAssetUrls(scenarioData, entrySceneId), {
        waitForVideo: false,
        onProgress: (loaded, total) => {
          setLoadingState((prev) => ({
            ...prev,
            progress: total === 0 ? 100 : Math.round((loaded / total) * 100),
          }));
        },
      });
      prefetchAssetUrls(collectRestOfChapterAssetUrls(scenarioData, entrySceneId));

      setLoadingState({
        isLoading: false,
        progress: 100,
        error: null,
      });
      
      onLoadComplete?.(scenarioData, scenarioId, missionId, layer);
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Unknown error');
      setLoadingState({
        isLoading: false,
        progress: 0,
        error: err,
        errorDetails: err.message,
      });
      onLoadError?.(err, scenarioId);
    }
  }, [apiBaseUrl, onLoadStart, onLoadComplete, onLoadError]);

  return {
    loadScenario,
    loadingState,
  };
}

